/* eslint-disable react/react-in-jsx-scope */
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { InfoCard } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import { usePermission } from '@backstage/plugin-permission-react';
import { rithumAgentStartTaskEntityPermission } from '@internal/plugin-rithum-agent-common';
import {
  rithumAgentApiRef,
  useRithumAgentOptions,
  dedupeModelsForPicker,
  normalizeModelForPickerState,
  compactModelButtonLabel,
  FALLBACK_OPTIONS,
} from '@internal/plugin-rithum-agent-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AccountTree from '@material-ui/icons/AccountTree';
import Android from '@material-ui/icons/Android';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { useMemo, useState } from 'react';
import type { JiraIssue } from '../api/JiraApi';
import { buildBatchedPrompt, branchSuffix } from './buildBatchedPrompt';

const SOURCE_LOC = 'backstage.io/source-location';
const PROJECT_SLUG = 'github.com/project-slug';

function sourceLocationForEntity(e: Entity): string | undefined {
  const ann = e.metadata.annotations ?? {};
  const sl = ann[SOURCE_LOC];
  if (sl) return sl;
  const slug = ann[PROJECT_SLUG];
  if (slug) return `https://github.com/${slug}`;
  return undefined;
}

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  popoverBody: {
    padding: theme.spacing(2),
    minWidth: 260,
  },
}));

interface Props {
  entity: Entity;
  selectedIssues: JiraIssue[];
}

export function AgentTaskCard({ entity, selectedIssues }: Props) {
  const classes = useStyles();
  const rithumAgentApi = useApi(rithumAgentApiRef);
  const alertApi = useApi(alertApiRef);
  const toastApi = useApi(toastApiRef);

  const { options } = useRithumAgentOptions();
  const [agentProvider, setAgentProvider] = useState(
    FALLBACK_OPTIONS.agents[0].id,
  );
  const [model, setModel] = useState('auto');
  const [agentName, setAgentName] = useState(FALLBACK_OPTIONS.agentNames[0]);
  const [extraInstructions, setExtraInstructions] = useState('');
  const [checkoutRef, setCheckoutRef] = useState('');
  const [branchNameOverride, setBranchNameOverride] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [branchAnchor, setBranchAnchor] = useState<HTMLElement | null>(null);
  const [botMenuAnchor, setBotMenuAnchor] = useState<HTMLElement | null>(null);
  const [modelMenuAnchor, setModelMenuAnchor] = useState<HTMLElement | null>(null);

  const entityRef = stringifyEntityRef(entity);

  const { allowed: canStartTask, loading: permissionLoading } = usePermission({
    permission: rithumAgentStartTaskEntityPermission,
    resourceRef: entityRef,
  });

  const models = useMemo(() => {
    const by = options.modelsByProvider?.[agentProvider];
    return by?.length ? by : FALLBACK_OPTIONS.modelsByProvider.cursor;
  }, [options.modelsByProvider, agentProvider]);

  const modelsForPicker = useMemo(
    () => dedupeModelsForPicker(models),
    [models],
  );

  const resolvedModel = useMemo(
    () => normalizeModelForPickerState(model, modelsForPicker),
    [model, modelsForPicker],
  );

  const handleSubmit = async () => {
    if (selectedIssues.length === 0) {
      alertApi.post({
        message: 'Select at least one Jira issue before starting a task.',
        severity: 'warning',
      });
      return;
    }

    const sl = sourceLocationForEntity(entity);
    if (!sl) {
      alertApi.post({
        message:
          'Entity has no backstage.io/source-location or github.com/project-slug annotation.',
        severity: 'error',
      });
      return;
    }

    setSubmitting(true);
    try {
      const agentPrompt = buildBatchedPrompt(selectedIssues, extraInstructions);
      const computedBranch =
        branchNameOverride.trim() || branchSuffix(selectedIssues);

      const { workflowRunUrl } = await rithumAgentApi.startTask({
        agentProvider,
        model: resolvedModel,
        agentName,
        agentPrompt,
        entityRef,
        sourceLocation: sl,
        checkoutRef: checkoutRef.trim() || undefined,
        branchName: computedBranch,
      });

      toastApi.post({
        title: `Agent task started for ${selectedIssues.map(i => i.key).join(', ')}`,
        status: 'success',
        timeout: 9000,
        ...(workflowRunUrl
          ? { links: [{ label: 'View workflow run', href: workflowRunUrl }] }
          : {}),
      });
    } catch (e) {
      alertApi.post({
        message: `Failed to start agent task: ${(e as Error).message}`,
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled =
    submitting ||
    permissionLoading ||
    !canStartTask ||
    selectedIssues.length === 0;

  return (
    <InfoCard title="Agent Task">
      {!permissionLoading && !canStartTask && (
        <Typography variant="body2" color="error" paragraph>
          Only owners of this entity may start an agent task.
        </Typography>
      )}

      {selectedIssues.length === 0 ? (
        <Typography variant="body2" color="textSecondary" paragraph>
          Select issues from the table to start a task.
        </Typography>
      ) : (
        <Typography variant="body2" paragraph>
          {selectedIssues.length} issue
          {selectedIssues.length !== 1 ? 's' : ''} selected:{' '}
          {selectedIssues.map(i => i.key).join(', ')}
        </Typography>
      )}

      <FormControl fullWidth margin="dense" variant="outlined" size="small">
        <InputLabel id="agent-provider-label">Agent Provider</InputLabel>
        <Select
          labelId="agent-provider-label"
          value={agentProvider}
          label="Agent Provider"
          onChange={e => setAgentProvider(e.target.value as string)}
        >
          {options.agents.map(a => (
            <MenuItem key={a.id} value={a.id} disabled={a.disabled}>
              {a.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        margin="dense"
        label="Additional instructions (optional)"
        placeholder="e.g. prioritise backward compatibility"
        multiline
        minRows={2}
        value={extraInstructions}
        onChange={e => setExtraInstructions(e.target.value)}
        variant="outlined"
        size="small"
      />

      <Box className={classes.row}>
        <Tooltip title="Checkout ref and branch name">
          <IconButton
            size="small"
            aria-label="Branch and checkout options"
            onClick={e => setBranchAnchor(e.currentTarget)}
          >
            <AccountTree fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Agent name">
          <IconButton
            size="small"
            aria-label="Agent name"
            onClick={e => setBotMenuAnchor(e.currentTarget)}
          >
            <Android fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button
          size="small"
          aria-label="Model"
          endIcon={<ArrowDropDown />}
          onClick={e => setModelMenuAnchor(e.currentTarget)}
          style={{ textTransform: 'none' }}
        >
          {compactModelButtonLabel(resolvedModel)}
        </Button>
      </Box>

      {/* Branch / checkout ref popover */}
      <Popover
        open={Boolean(branchAnchor)}
        anchorEl={branchAnchor}
        onClose={() => setBranchAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={classes.popoverBody}>
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Checkout ref"
            placeholder="main"
            value={checkoutRef}
            onChange={e => setCheckoutRef(e.target.value)}
            variant="outlined"
          />
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Branch name suffix"
            placeholder={
              selectedIssues.length > 0 ? branchSuffix(selectedIssues) : 'jira-…'
            }
            helperText="Workflow adds cerebro/ prefix"
            value={branchNameOverride}
            onChange={e => setBranchNameOverride(e.target.value)}
            variant="outlined"
          />
        </div>
      </Popover>

      {/* Agent name menu */}
      <Menu
        anchorEl={botMenuAnchor}
        open={Boolean(botMenuAnchor)}
        onClose={() => setBotMenuAnchor(null)}
      >
        {options.agentNames.map(n => (
          <MenuItem
            key={n}
            selected={n === agentName}
            onClick={() => {
              setAgentName(n);
              setBotMenuAnchor(null);
            }}
          >
            {n}
          </MenuItem>
        ))}
      </Menu>

      {/* Model menu */}
      <Menu
        anchorEl={modelMenuAnchor}
        open={Boolean(modelMenuAnchor)}
        onClose={() => setModelMenuAnchor(null)}
      >
        {modelsForPicker.map(m => (
          <MenuItem
            key={m}
            selected={m === resolvedModel}
            onClick={() => {
              setModel(m);
              setModelMenuAnchor(null);
            }}
          >
            {m}
          </MenuItem>
        ))}
      </Menu>

      <Box mt={2}>
        <Tooltip
          title={
            // eslint-disable-next-line no-nested-ternary
            selectedIssues.length === 0
              ? 'Select at least one issue'
              : !canStartTask && !permissionLoading
                ? 'Only entity owners can start an agent task'
                : ''
          }
        >
          <span>
            <Button
              color="primary"
              variant="contained"
              fullWidth
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              {submitting ? 'Starting…' : 'Start task'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </InfoCard>
  );
}
